import * as THREE from 'three';
import { clearPreviewTint } from './previewTint';

const EPS = 1e-9;
const CM_TO_UNIT = 1;

// Opacity of the band tint over a mesh's own vertex colours — low enough that
// markings on the socket stay readable through the band.
const BAND_TINT_STRENGTH = 0.45;

// --- ML/AP helpers ---
const UP = new THREE.Vector3(0, 1, 0);

function _getWorldPos(mesh, out = new THREE.Vector3()) {
  if (!mesh?.getWorldPosition) return null;
  mesh.getWorldPosition(out);
  return out;
}

export function _computeAxes(points) {
  // Default axes if landmarks are missing
  let uML = new THREE.Vector3(1, 0, 0);
  let uAP = new THREE.Vector3(0, 0, 1);

  const pInt = _getWorldPos(points?.internalCondyle?.sphere, new THREE.Vector3());
  const pExt = _getWorldPos(points?.externalCondyle?.sphere, new THREE.Vector3());

  if (pInt && pExt) {
    const vML = new THREE.Vector3().subVectors(pExt, pInt);
    vML.y = 0; // project to transverse plane
    if (vML.lengthSq() > 1e-10) uML.copy(vML).normalize();

    // AP = Up × ML (in plane)
    uAP.copy(new THREE.Vector3().crossVectors(UP, uML)).normalize();

    // Orient AP toward patella if available
    const pMid = _getWorldPos(points?.midPatellar?.sphere, new THREE.Vector3());
    if (pMid) {
      const midCond = new THREE.Vector3().addVectors(pExt, pInt).multiplyScalar(0.5);
      const vPat = pMid.sub(midCond); vPat.y = 0;
      if (vPat.lengthSq() > 1e-10 && vPat.normalize().dot(uAP) < 0) uAP.multiplyScalar(-1);
    }
  }

  // Fallback if AP degenerate
  if (!isFinite(uAP.x) || !isFinite(uAP.y) || !isFinite(uAP.z) || uAP.lengthSq() < 1e-10) {
    uAP.set(0, 0, 1);
  }
  return { uML, uAP };
}

// Uses the ring geometry built (positions are relative to centroid)
export function _computeMLAPFromBuilt(built, uML, uAP) {
  // Prefer thin (BufferGeometry with .attributes.position); works in all cases
  const posAttr = built?.thin?.geometry?.attributes?.position;
  if (!posAttr?.array || posAttr.count === 0) return { mlM: 0, apM: 0 };

  const arr = posAttr.array; // [x,y,z] relative to centroid (world frame)
  let minML = Infinity, maxML = -Infinity, minAP = Infinity, maxAP = -Infinity;

  // y offsets are ~0 for the slice; ignore y in dot
  for (let i = 0; i < arr.length; i += 3) {
    const x = arr[i], z = arr[i + 2];
    const sML = x * uML.x + z * uML.z;
    const sAP = x * uAP.x + z * uAP.z;
    if (sML < minML) minML = sML;
    if (sML > maxML) maxML = sML;
    if (sAP < minAP) minAP = sAP;
    if (sAP > maxAP) maxAP = sAP;
  }
  return {
    mlM: (maxML - minML) || 0,   // meters
    apM: (maxAP - minAP) || 0,   // meters
  };
}

// after built the slice geometry `built` (or you have the raw intersection points)
// compute centroid in WORLD, then convert to LOCAL for shadedMesh
export function computeSliceCentroidLocalwBuilt(built, shadedMesh, grp) {
  // Try to use the line/segment positions (world) to compute arithmetic mean
  const posAttr = built?.thin?.geometry?.attributes?.position;
  if (!posAttr?.array || posAttr.count === 0) return null;

  // Ensure matrices are up-to-date before calculations
  shadedMesh.updateMatrixWorld(true);
  if (grp) grp.updateMatrixWorld(true);

  const mWorld = grp?.matrixWorld ?? shadedMesh.matrixWorld;
  const v = new THREE.Vector3();
  let sx = 0, sz = 0, c = 0;
  for (let i=0;i<posAttr.array.length;i+=3){
    v.set(posAttr.array[i], posAttr.array[i+1], posAttr.array[i+2]).applyMatrix4(mWorld);
    sx += v.x; sz += v.z; c++;
  }
  if (c === 0) return null;
  const world = new THREE.Vector3(sx/c, 0, sz/c);
  const local = world.clone();
  shadedMesh.worldToLocal(local);
  return { x: local.x, z: local.z };
}

export function computeSliceCentroidLocal(shadedMesh, grp) {
  if (!shadedMesh || !grp) return null;
  // Ensure both matrices are up-to-date before calculations
  shadedMesh.updateMatrixWorld(true);
  grp.updateMatrixWorld(true);
  const w = new THREE.Vector3();
  grp.getWorldPosition(w);                    // ring group is positioned at the slice centroid
  const l = w.clone();
  shadedMesh.worldToLocal(l);                 // convert to shadedMesh local space
  return { x: l.x, z: l.z };
}

// Build a height→scale,centroid LUT from rings.
//
// "Measure between lines" model: each ring defines the exact circumference scale
// at its own height, and the socket transitions *between* consecutive rings
// (piecewise interpolation) rather than each ring deforming a fixed symmetric
// ±radius band. Each ring's `falloff` is simply the spacing down to the ring
// below it (the "distance to the next line") and is always derived from ring
// positions — there is no independent override. Typing a falloff in the tool
// card repositions the neighbouring ring so the gap matches.
export function buildLUTFromRings(shadedMesh, zeroY, measurements, defaultFalloffCm = 1, profile = 'hann') {
  if (!shadedMesh || !measurements?.length) return null;
  const rows = [];

  for (const m of measurements) {
    const yWorld = zeroY + (m.height ?? 0) * CM_TO_UNIT; // cm→m
    const scanC  = m?.scan?.circumference || 0;
    const rectC  = m?.rectified?.circumference || scanC;
    const s      = scanC > 1e-9 ? (rectC / scanC) : 1;

    // centroid (use cached if present, already in local space)
    const cx = m._centroidLocal?.x ?? 0;
    const cz = m._centroidLocal?.z ?? 0;

    // Persisted per-line colour (same value drawn as the ring on the socket) so
    // the preview can tint each modified band with its owning ring's colour.
    rows.push({ y: yWorld, scale: s, c: { x: cx, z: cz }, color: m.color });
  }

  rows.sort((a,b)=>a.y - b.y);

  const n = rows.length;
  const ys = rows.map(r=>r.y);
  // Falloff is always the ring spacing (distance to the next line); there is no
  // independent override — typing a falloff repositions the neighbouring ring.
  const falloffs = rows.map((r, j) => {
    let auto;
    if (n === 1)      auto = defaultFalloffCm * CM_TO_UNIT;
    else if (j > 0)   auto = ys[j] - ys[j-1];   // gap down to the ring below
    else              auto = ys[1] - ys[0];     // bottom ring: mirror the gap above
    return Math.max(auto, 1e-6);
  });

  return {
    ys,
    scales: rows.map(r=>r.scale),
    centroids: rows.map(r=>r.c),
    falloffs,
    // Per-ring colours in the SAME sorted order as ys/scales/centroids, so the
    // `k` index sampleProfile returns maps a modified vertex to its owning
    // ring's colour. Without this the preview can't tint the bands.
    colors: rows.map(r=>r.color),
    profile,
  };
}

// Flat-topped taper: how far a ring's change has fallen away at `t`, where t is
// 0 on the ring line and 1 at the far end of its fade. 0 (still full value)
// across the plateau, then smootherstep to 1 at the end. Zero slope at both
// ends of the skirt, so the value leaves the line slowly, does its travelling
// through the middle, and settles onto zero — rather than a Hann bump, which
// starts dropping the moment it leaves the peak.
const BAND_PLATEAU = 0.35; // fraction of the fade held at the full value
function taper01(t){
  t = Math.min(Math.max(t, 0), 1);
  if (t <= BAND_PLATEAU) return 0;
  const u = (t - BAND_PLATEAU) / (1 - BAND_PLATEAU);
  return u*u*u*(u*(u*6 - 15) + 10); // smootherstep
}

// Monotone 0→1 blend between two ring values (smootherstep: C1 at both rings,
// so the surface has no crease where a gap meets a ring line).
function blend01(u){
  u = Math.min(Math.max(u, 0), 1);
  return u*u*u*(u*(u*6 - 15) + 10);
}

// Sample the circumference-scale profile at a world-Y height.
//
// Model: a ring's change lands EXACTLY ON ITS OWN LINE — the % typed for a line
// is the % that line's circumference actually loses, which is what the tool
// card reports and what the Circumference tool re-measures afterwards. Between
// two rings the two changes blend into each other, so the socket runs smoothly
// from one reading to the next. Past the outer ends of the stack there is
// nothing to blend with, so the outermost ring's change fades to zero over its
// own falloff — below the bottom-most ring toward the distal tip, and above the
// top-most ring toward the brim.
//
// Returns { s, cx, cz, k, w } or null (untouched).
function sampleProfile(yWorld, lut){
  const { ys, scales, centroids, falloffs } = lut;
  const n = ys.length;
  if (!n) return null;

  const emit = (d, cx, cz, k, w) => {
    const s = Math.max(0.05, 1 + d); // clamped: avoid inversion
    if (Math.abs(s - 1) < 1e-6) return null;
    return { s, cx, cz, k, w };
  };

  // Outer end of the stack: ring `k`'s change at full strength on its line,
  // fading to nothing `span` away from it.
  const fadeFrom = (k, dist, span) => {
    if (dist >= span) return null;
    const w = 1 - taper01(dist / Math.max(span, 1e-9));
    return emit((scales[k] - 1) * w, centroids[k].x, centroids[k].z, k, w);
  };

  if (yWorld >= ys[n-1]) return fadeFrom(n-1, yWorld - ys[n-1], falloffs[n-1]);
  if (yWorld <= ys[0])   return fadeFrom(0, ys[0] - yWorld, falloffs[0]);

  // Interior: locate the gap ys[j-1] < yWorld < ys[j] and blend the two rings'
  // changes across it. At either end of the gap the blend is 0 or 1, so each
  // ring line gets its own value exactly.
  let j = 1;
  while (j < n && ys[j] <= yWorld) j++;
  const lo = j - 1, hi = j;
  const t = blend01((yWorld - ys[lo]) / Math.max(ys[hi] - ys[lo], 1e-9));
  const d = (scales[lo] - 1) + ((scales[hi] - 1) - (scales[lo] - 1)) * t;
  // The scaling axis moves with the readings, so it is the ring's own slice
  // centroid on its own line and travels across the gap on the same blend.
  const cx = centroids[lo].x + (centroids[hi].x - centroids[lo].x) * t;
  const cz = centroids[lo].z + (centroids[hi].z - centroids[lo].z) * t;
  // `k` is the nearer ring (whose colour the preview tints this vertex with).
  return emit(d, cx, cz, t < 0.5 ? lo : hi, 1 - Math.abs(2*t - 1));
}

// Deform the preview clone (NOT the scan mesh). This only reshapes the clone to
// preview the reduced socket — the band COLOURS are painted separately by
// paintBandZones (on both the socket and this clone), not here.
export function deformPreviewMesh(previewMesh, lut, basePositions){
  if (!previewMesh || !lut) return;
  const pos = previewMesh.geometry.attributes.position;
  const arr = pos.array;

  // Always start from original to avoid cumulative growth
  if (basePositions && basePositions.length === arr.length) arr.set(basePositions);

  const { ys } = lut;
  if (!ys?.length) return;

  // Ensure matrixWorld is up-to-date before using it for calculations
  // This is critical during initialization when the mesh was just added to the scene
  previewMesh.updateMatrixWorld(true);

  // After alignment, geometry is baked and mesh has identity transform
  // So we need to convert local Y to world Y using the mesh transform
  const mw = previewMesh.matrixWorld;
  const mwInv = new THREE.Matrix4().copy(mw).invert();
  const localVec = new THREE.Vector3();
  const worldVec = new THREE.Vector3();
  const worldCentroid = new THREE.Vector3();
  const worldVertex = new THREE.Vector3();

  for (let i=0;i<arr.length;i+=3){
    const x0 = arr[i], y0 = arr[i+1], z0 = arr[i+2];

    // Convert local vertex position to world space
    localVec.set(x0, y0, z0);
    worldVec.copy(localVec).applyMatrix4(mw);
    const yWorld = worldVec.y;  // Use world Y coordinate

    // Sample the piecewise profile: exact scale at each ring, interpolated
    // between consecutive rings, tapered to 1 past the top/bottom rings.
    const sample = sampleProfile(yWorld, lut);
    if (!sample) continue;
    const { s, cx, cz } = sample;

    // Convert local centroid to world space using the vertex's local Y, then
    // pin the centroid's world X,Z to the vertex's world Y.
    localVec.set(cx, y0, cz).applyMatrix4(mw);
    worldCentroid.set(localVec.x, yWorld, localVec.z);

    // Apply scaling in world XZ plane (perpendicular to world Y)
    const dx_world = worldVec.x - worldCentroid.x;
    const dz_world = worldVec.z - worldCentroid.z;

    const newWorldX = worldCentroid.x + dx_world * s;
    const newWorldZ = worldCentroid.z + dz_world * s;

    // Convert back to local space (keep world Y unchanged)
    worldVertex.set(newWorldX, worldVec.y, newWorldZ);
    worldVertex.applyMatrix4(mwInv);

    arr[i  ] = worldVertex.x;
    arr[i+1] = worldVertex.y;
    arr[i+2] = worldVertex.z;
  }

  pos.needsUpdate = true;

  // **CRITICAL FIX**: Update all spatial data structures after geometry modification
  previewMesh.geometry.computeVertexNormals();
  previewMesh.geometry.computeBoundingBox();
  previewMesh.geometry.computeBoundingSphere();

  // **CRITICAL FIX**: Dispose BVH tree if it exists (will be rebuilt when needed)
  // This prevents stale spatial acceleration structures from causing bugs in subsequent tools
  if (previewMesh.geometry.boundsTree) {
    previewMesh.geometry.disposeBoundsTree();
  }
}

// Which ring's strap a given world-Y belongs to, or -1 for none.
//
// A strap HANGS BELOW its line: the ring line is the strap's TOP edge, and the
// strap runs down to the next line below it — which is exactly that ring's
// `falloff` ("distance to the next line"). The bottom-most ring has no line
// below it, so its strap is one falloff deep. Nothing above the top-most line
// belongs to a strap. Straps are therefore contiguous and non-overlapping, and
// every line renders on the top edge of its own band.
function sampleZone(yWorld, lut){
  const { ys, falloffs } = lut;
  const n = ys?.length || 0;
  if (!n) return -1;
  if (yWorld > ys[n-1]) return -1;                                            // above the top line
  if (yWorld <= ys[0])  return (ys[0] - yWorld) <= falloffs[0] ? 0 : -1;      // bottom ring's strap
  let j = 0;
  while (j < n && ys[j] < yWorld) j++;   // first line at or above yWorld…
  return j < n ? j : -1;                 // …owns the strap hanging down over it
}

// Preview-only: tint a mesh's vertex colours so each ring's band zone takes that
// line's colour, while everything outside any band keeps its `baseColors` value.
// Used for BOTH the real socket (baseColors = the socket's untouched colours, so
// unmodified areas keep their own colour) and the shape-preview clone (baseColors
// = solid white, so the clone shows the band colours over white instead of the
// dark raw scan colours). The mesh material must render with vertexColors. Undo
// the socket variant with restoreSocketColors.
//
// `strength` is the band colour's opacity over baseColors (0..1). The socket
// carries pen markings in its vertex colours, so a fully opaque band would hide
// them — blend instead and they read through the tint. The clone paints over
// solid white and has nothing to preserve, so it passes 1.
export function paintBandZones(mesh, lut, baseColors, strength = BAND_TINT_STRENGTH){
  if (!mesh || !lut || !baseColors) return;
  const a = Math.min(1, Math.max(0, strength));
  const geom = mesh.geometry;
  const colAttr = geom.attributes.color;
  const posAttr = geom.attributes.position;
  if (!colAttr || !posAttr || colAttr.array.length !== baseColors.length) return;

  const ringColors = (lut.colors || []).map(hex =>
    new THREE.Color(hex || 0x008eff).convertSRGBToLinear());

  mesh.updateMatrixWorld(true);
  const mw = mesh.matrixWorld;
  const v = new THREE.Vector3();

  for (let i = 0; i < posAttr.count; i++){
    const b = i * 3;
    let r = baseColors[b], g = baseColors[b+1], bl = baseColors[b+2]; // untouched colour
    v.set(posAttr.getX(i), posAttr.getY(i), posAttr.getZ(i)).applyMatrix4(mw);
    const k = sampleZone(v.y, lut);
    if (k >= 0 && ringColors[k]){
      const c = ringColors[k];
      r  += (c.r  - r)  * a;
      g  += (c.g  - g)  * a;
      bl += (c.b - bl) * a;
    }
    colAttr.setXYZ(i, r, g, bl);
  }
  colAttr.needsUpdate = true;
}

// Revert a mesh's colours to the snapshot taken before paintBandZones.
export function restoreSocketColors(shadedMesh, baseColors){
  if (!shadedMesh || !baseColors) return;
  const colAttr = shadedMesh.geometry?.attributes?.color;
  if (!colAttr || colAttr.array.length !== baseColors.length) return;
  colAttr.array.set(baseColors);
  colAttr.needsUpdate = true;
  // The live attribute is clean again, so the marker geometry clones use to strip
  // the tint (see lib/previewTint) has nothing left to describe.
  clearPreviewTint(shadedMesh.geometry);
}